import React, { useState } from "react";
import { motion } from "framer-motion";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xvzbqpag";

const STATUS = { IDLE: "idle", SUBMITTING: "submitting", SUCCESS: "success", ERROR: "error" };

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState(STATUS.IDLE);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(STATUS.SUBMITTING);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        setStatus(STATUS.SUCCESS);
        setForm({ name: "", email: "", message: "" });
      } else {
        setStatus(STATUS.ERROR);
      }
    } catch {
      setStatus(STATUS.ERROR);
    }
  };

  return (
    <section id="contact" className="bg-[#f4f4f4] dark:bg-[#181818] py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-[#053676] dark:text-[#2A71E8] font-sans font-bold text-3xl sm:text-4xl mb-4">
            Get In Touch
          </h2>
          <p className="text-[#09062C] dark:text-gray-300 font-sans font-medium text-lg max-w-2xl mx-auto">
            Have questions or want to learn more? We'd love to hear from you.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-white dark:bg-black rounded-[20px] aboutUsShadow p-8"
        >
          {status === STATUS.SUCCESS ? (
            <div className="text-center py-10">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-[#09062C] dark:text-white font-sans font-bold text-xl mb-2">
                Message Sent!
              </h3>
              <p className="text-[#6A6A6A] dark:text-gray-400 font-sans text-[15px] mb-6">
                Thank you for reaching out. We'll get back to you as soon as possible.
              </p>
              <button
                onClick={() => setStatus(STATUS.IDLE)}
                className="text-[#2A71E8] font-sans font-medium text-sm hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {status === STATUS.ERROR && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-[12px] px-4 py-3 text-red-600 dark:text-red-400 font-sans text-sm">
                  Something went wrong. Please try again or email us directly.
                </div>
              )}

              <div>
                <label className="font-sans font-bold text-[14px] text-[#09062C] dark:text-[#6A6A6A] mb-1 block">
                  Name
                </label>
                <div className="border border-[#8a8a8a] rounded-[20px] flex items-center px-4">
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="bg-transparent p-3 w-full focus:outline-none dark:text-white text-black font-sans"
                  />
                </div>
              </div>

              <div>
                <label className="font-sans font-bold text-[14px] text-[#09062C] dark:text-[#6A6A6A] mb-1 block">
                  Email
                </label>
                <div className="border border-[#8a8a8a] rounded-[20px] flex items-center px-4">
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="bg-transparent p-3 w-full focus:outline-none dark:text-white text-black font-sans"
                  />
                </div>
              </div>

              <div>
                <label className="font-sans font-bold text-[14px] text-[#09062C] dark:text-[#6A6A6A] mb-1 block">
                  Message
                </label>
                <div className="border border-[#8a8a8a] rounded-[20px] px-4">
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    rows={5}
                    required
                    className="bg-transparent p-3 w-full focus:outline-none dark:text-white text-black font-sans resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={status === STATUS.SUBMITTING}
                className="bg-[#2A71E8] text-white font-sans font-bold py-3 px-6 rounded-[20px] text-lg hover:bg-[#1e5bb5] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === STATUS.SUBMITTING ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
